import React from 'react';

const JobAssistance = () => {
  return (
    <div className="text-sm leading-relaxed text-justify">
      <h1 className="text-base font-bold">Students Agreement Copy – Awdiz Job Assistance Program</h1>
      <p className="mt-2">
        This Agreement applies to all candidates enrolling in Awdiz Job Assistance Programs, where the primary focus is on skill development, interview preparation, career guidance, and structured placement assistance only.
      </p>
      <p className="mt-2">
        By enrolling in this program, the student acknowledges and agrees that Awdiz only provides job assistance and placement support, and the final outcome depends entirely on the student's performance, eligibility, interview clearance, conduct, and the hiring company's selection criteria.
      </p>
      <p className="mt-2">
        By enrolling, the student agrees to follow all rules, policies, academic requirements, and professional standards defined by Awdiz.
      </p>

      <h2 className="!mt-6 font-semibold">Program Objective</h2>
      <p className="mt-2">
        The Job Assistance Program is designed to:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Build strong technical and practical skills</li>
        <li>Prepare students for real-world interviews</li>
        <li>Provide structured placement assistance</li>
        <li>Improve employability through continuous training, guidance, and support</li>
      </ul>
      <p className="mt-2">
        Awdiz supports students through resume guidance, mock interviews, interview coordination, job opportunity sharing, and general career support. Final job selection depends on the student's own merit and the employer's decision.
      </p>

      <h3 className="!mt-6"><strong>Terms & Conditions – Job Assistance Program</strong></h3>

      <h4 className="!mt-4 font-semibold">1. Nature of Program</h4>
      <p className="mt-1">
        This is strictly a Job Assistance Program.
      </p>
      <p className="mt-1">
        Awdiz shall provide support in the form of:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Technical training</li>
        <li>Practical skill development</li>
        <li>Resume building</li>
        <li>Mock interviews</li>
        <li>Soft skills preparation</li>
        <li>Interview scheduling support</li>
        <li>Sharing of relevant job opportunities</li>
      </ul>
      <p className="mt-1">
        Awdiz shall not be held responsible for guaranteeing job placement, salary level, role preference, company preference, location preference, or timeline of selection.
      </p>

      <h4 className="!mt-4 font-semibold">2. Training & Learning</h4>
      <p className="mt-1">
        Training may be delivered through:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Classroom sessions</li>
        <li>Online sessions</li>
        <li>Hybrid mode</li>
        <li>Recorded sessions</li>
        <li>LMS-based learning support</li>
      </ul>
      <p className="mt-1">
        LMS access may be provided for tracking and managing:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Attendance</li>
        <li>Assignments</li>
        <li>Assessments</li>
        <li>Mock interviews</li>
        <li>Interview preparation activities</li>
      </ul>

      <h4 className="!mt-4 font-semibold">3. Attendance & Performance</h4>
      <p className="mt-1">
        Students are strongly encouraged to maintain at least 80% attendance, though attendance alone does not guarantee interview opportunities or selection.
      </p>
      <p className="mt-1">
        Students are expected to:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Attend classes regularly</li>
        <li>Complete assignments on time</li>
        <li>Participate in mock interviews</li>
        <li>Attend soft skills and interview preparation sessions</li>
        <li>Maintain seriousness and consistency throughout the program</li>
      </ul>
      <p className="mt-1">
        Awdiz reserves the right to decide whether a student is sufficiently prepared and eligible to be considered for placement assistance opportunities.
      </p>
      <p className="mt-2 font-semibold">
        Important Note: Better performance, stronger skills, and professional discipline directly improve the student's chances of receiving interview opportunities and getting selected. Poor performance may reduce or delay placement assistance.
      </p>

      <h4 className="!mt-4 font-semibold">4. Assessments & Eligibility</h4>
      <p className="mt-1">
        Regular tests, assignments, practical evaluations, mock interviews, and internal assessments may be conducted during the program.
      </p>
      <p className="mt-1">
        Awdiz reserves the right to determine whether a student is interview-ready and eligible for job opportunities based on:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Technical knowledge</li>
        <li>Practical performance</li>
        <li>Communication skills</li>
        <li>Mock interview performance</li>
        <li>Assignment completion</li>
        <li>Professional conduct</li>
        <li>Overall readiness for hiring</li>
      </ul>
      <p className="mt-1">
        Students who are not found ready, eligible, or suitable for interviews may not be forwarded to hiring companies until improvement is observed.
      </p>

      <h4 className="!mt-4 font-semibold">5. Placement Assistance Scope</h4>
      <p className="mt-1">
        Awdiz may provide the following placement assistance support:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Resume building support</li>
        <li>Profile improvement guidance</li>
        <li>Mock interview practice</li>
        <li>Soft skills support</li>
        <li>Interview scheduling support</li>
        <li>Sharing of job openings and company requirements</li>
        <li>General career guidance</li>
      </ul>
      <p className="mt-1">
        Placement assistance is limited to support activities only. Awdiz does not promise any minimum number of interviews, job calls, job offers, or joining confirmations.
      </p>
      <p className="mt-1">
        The number of interviews or job opportunities shared depends on:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Student performance</li>
        <li>Student eligibility</li>
        <li>Student readiness</li>
        <li>Market conditions</li>
        <li>Employer requirements</li>
        <li>Open positions available at the time</li>
      </ul>

      <h4 className="!mt-4 font-semibold">6. Candidate Responsibility</h4>
      <p className="mt-1">
        The student is fully responsible for making the best use of the assistance provided by Awdiz.
      </p>
      <p className="mt-1">
        The student must:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Actively apply to jobs shared by Awdiz</li>
        <li>Attend all scheduled interviews on time</li>
        <li>Respond to calls, emails, and messages within 24 hours</li>
        <li>Maintain professional behaviour with Awdiz staff and employers</li>
        <li>Be honest in communication and documentation</li>
        <li>Prepare sincerely for interviews</li>
        <li>Remain flexible regarding entry-level opportunities</li>
      </ul>
      <p className="mt-1">
        Failure to do so may affect, delay, suspend, or discontinue placement assistance support at Awdiz's sole discretion.
      </p>

      <h4 className="!mt-4 font-semibold">7. Job Assistance Only</h4>
      <p className="mt-1">
        The student clearly understands, acknowledges, and agrees that this program is a Job Assistance Program only.
      </p>
      <p className="mt-1">
        Awdiz provides Job Assistance only, which may include resume support, interview preparation, mock interviews, job opportunity sharing, and interview scheduling support. Under no circumstances shall this program be interpreted as a job guarantee, employment guarantee, or assured placement commitment.
      </p>
      <p className="mt-1">
        The student further understands and accepts that:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Awdiz does not promise assured placement</li>
        <li>Student is responsible if they are not selected by any company</li>
        <li>Awdiz is not responsible for interview rejection, delayed hiring, lack of openings, company hiring freezes, or employer-side decisions</li>
        <li>Job Assistance will depend on the student's performance, readiness, eligibility, and interview qualification</li>
      </ul>
      <p className="mt-1">
        Final selection depends entirely on:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>The student's technical and practical capability</li>
        <li>Communication skills and interview performance</li>
        <li>Behaviour, discipline, and professionalism</li>
        <li>Employer assessment criteria</li>
        <li>Employer hiring requirements and business decisions</li>
      </ul>
      <p className="mt-1">
        Awdiz's responsibility is limited strictly to providing Job Assistance only, and not guaranteed employment or assured placement.
      </p>

      <h4 className="!mt-4 font-semibold">8. Job Acceptance & Opportunity Fit</h4>
      <p className="mt-1">
        Students are expected to seriously consider job opportunities shared by Awdiz, especially entry-level opportunities relevant to their course and skill set.
      </p>
      <p className="mt-1">
        Awdiz shall not be responsible for:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Student salary expectations</li>
        <li>Preferred company choices</li>
        <li>Preferred job title</li>
        <li>Preferred location</li>
        <li>Preferred shift timings</li>
        <li>Rejection of available opportunities by the student</li>
      </ul>
      <p className="mt-1">
        Refusal to attend or accept suitable opportunities may impact future placement assistance.
      </p>

      <h4 className="!mt-4 font-semibold">9. Placement Timeline</h4>
      <p className="mt-1">
        Placement assistance may begin during or after training, depending on the student's readiness and hiring market conditions.
      </p>
      <p className="mt-1">
        No fixed placement timeline is promised or guaranteed by Awdiz.
      </p>
      <p className="mt-1">
        Awdiz shall not be held responsible for any delay in interviews, job calls, or final selection.
      </p>

      <h4 className="!mt-4 font-semibold">10. Internship Opportunities</h4>
      <p className="mt-1">
        Awdiz may share internship opportunities from time to time, whether paid or unpaid, based on availability and employer requirements.
      </p>
      <p className="mt-1">
        Internship opportunities are also part of job assistance support only and do not guarantee permanent employment.
      </p>

      <h4 className="!mt-4 font-semibold">11. Fees & Payment Policy</h4>
      <p className="mt-1">
        All program fees paid to Awdiz are non-refundable and non-transferable under any circumstances, unless otherwise stated in writing by management.
      </p>
      <p className="mt-1">
        Delay or default in payment may result in:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>LMS access restriction</li>
        <li>Suspension of training access</li>
        <li>Withholding of support services</li>
        <li>Delay or restriction in placement assistance</li>
      </ul>
      <p className="mt-1">
        Full fee payment compliance may be required before completion formalities or continued support.
      </p>

      <h4 className="!mt-4 font-semibold">12. Batch Change Policy</h4>
      <p className="mt-1">
        Batch changes are allowed only in valid and exceptional cases, subject to management approval.
      </p>
      <p className="mt-1">
        Awdiz may apply administrative charges for any approved batch transfer.
      </p>

      <h4 className="!mt-4 font-semibold">13. Code of Conduct</h4>
      <p className="mt-1">
        Students are expected to maintain proper discipline, professionalism, and respectful conduct throughout the program.
      </p>
      <p className="mt-1">
        Any misconduct, misbehaviour, false commitments, abusive language, indiscipline, or actions harming Awdiz's reputation may lead to:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Suspension</li>
        <li>Termination from training/support</li>
        <li>Permanent discontinuation of placement assistance</li>
        <li>No refund of fees</li>
      </ul>

      <h4 className="!mt-4 font-semibold">14. Course Completion</h4>
      <p className="mt-1">
        The course shall be considered completed only when:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Training modules are completed</li>
        <li>Required assessments are attempted as applicable</li>
        <li>All dues and fees are fully paid</li>
      </ul>
      <p className="mt-1">
        Completion of the course does not mean job placement is guaranteed.
      </p>

      <h4 className="!mt-4 font-semibold">15. Limitation of Liability</h4>
      <p className="mt-1">
        Awdiz shall not be held liable for:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>No job offer being received</li>
        <li>Delay in receiving interviews</li>
        <li>Delay in hiring processes</li>
        <li>Rejection by companies</li>
        <li>Market slowdown</li>
        <li>Lack of openings</li>
        <li>Student not being interview-ready</li>
        <li>Student failing interviews</li>
        <li>Student refusing opportunities</li>
        <li>Salary/package lower than student expectations</li>
        <li>Role, company, or location mismatch with student preference</li>
      </ul>
      <p className="mt-1">
        Awdiz's role is limited strictly to training and placement assistance support, and not guaranteed employment.
      </p>

      <h4 className="!mt-4 font-semibold">16. Promotional Usage</h4>
      <p className="mt-1">
        Awdiz may use student-related information such as:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Name</li>
        <li>Photograph</li>
        <li>Course details</li>
        <li>Placement or internship details</li>
        <li>Testimonials or reviews</li>
      </ul>
      <p className="mt-1">
        for purposes including:
      </p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Marketing</li>
        <li>Branding</li>
        <li>Social media</li>
        <li>Website promotion</li>
        <li>Institutional communication</li>
      </ul>
      <p className="mt-1">
        unless otherwise restricted by written agreement.
      </p>

      <h4 className="!mt-4 font-semibold">17. Jurisdiction</h4>
      <p className="mt-1">
        All disputes, claims, and matters arising out of this agreement shall be subject exclusively to the jurisdiction of the courts of Mumbai, Maharashtra.
      </p>

      <h4 className="!mt-6 font-semibold">Important Notes</h4>
      <p className="mt-2 font-semibold">
        Note: Complete fee payment is mandatory to apply for interviews. Candidate with pending fees (for any reason) will strictly not be allowed for any interviews arranged by Awdiz. Center Manager approval for complete full payment needs to be submitted to placement team for initiating interviews after training.
      </p>
      <p className="mt-4">
        Kindly take admission at AWDIZ if the clauses of all the terms and conditions mentioned above are satisfying to you. Once you are admitted to our institute means you have read all our terms and conditions and you are agreeing with it.
      </p>
      <p className="mt-4">
        I/We hereby declare that the information given by me on the Online/Offline Registration / Application Form etc. is correct to the best of my knowledge and belief. I/We understand that in the event of any information found to be incorrect or false, my admission may be cancelled.
      </p>
      <p className="mt-4">
        I/We the Father/Mother/ or the student hereby severally and jointly declare that I / we have read and understood all the clauses contained in the Declaration on the Registration and agree to abide by them without any reservation or ambiguity and I/We have taken a print of all the terms and conditions for my future reference.
      </p>
      <p className="mt-4 font-semibold">
        NOTE: This contract is valid for a period of 12 Months from the date of signing the contract.
      </p>
      <p className="mt-1">
        Any legal matter/dispute is subject to Mumbai Jurisdiction only.
      </p>
      <p className="mt-2">
        <strong>AWDIZ Office Address 1:</strong> 2nd Floor, Vashi Plaza Building, Office no. 421A, A Wing, Sector 17, Vashi, Navi Mumbai, Maharashtra 400703
      </p>
      <p className="mt-1">
        <strong>Awdiz Office Address 2:</strong> Ground floor, A 791, KC Marg, next to 81 Aureate, Reclamation, Bandra West, Mumbai, Maharashtra 400050
      </p>
      <p className="mt-2 font-semibold">
        Note: Kindly submit 2 Passport Size Photographs, One Digital Photograph for online records, PAN Card and Aadhar/ Driving License photocopy along with signed copy of this agreement.
      </p>
    </div>
  );
};

export default JobAssistance;
